import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';

import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  AddReactionRequest,
  FamilyMember,
  MasterListData,
  ReactionTypeMasterItem,
  ReportReasonItem,
  RemoveReactionRequest,
  ShareStoryWithMembersRequest,
  StoryReaction,
  TagStoryWithMembersRequest,
  StoryDetail as StoryDetailData,
  UpdateTranscriptRequest,
} from '../../../../core/models/story.models';

/** Maps a reaction type id (from master list) to its display emoji. */
const REACTION_EMOJI: Record<number, string> = {
  1: '❤️',  // Heart
  2: '👍',  // Like
  3: '🎉',  // Celebrate
  4: '🤗',  // Support
};

@Component({
  selector: 'app-story-detail',
  templateUrl: './story-detail.html',
  styleUrl: './story-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, FormsModule],
})
export class StoryDetail implements OnInit, AfterViewInit, OnDestroy {

  // ── ViewChild refs (Vimeo player) ───────────────────────────────────────
  @ViewChild('vimeoIframe') vimeoIframeRef?: ElementRef<HTMLIFrameElement>;
  @ViewChild('seekBar')     seekBarRef?:    ElementRef<HTMLInputElement>;

  // ── Dependency Injection ───────────────────────────────────────────────────
  private readonly api        = inject(ApiService);
  private readonly route      = inject(ActivatedRoute);
  private readonly router     = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly sanitizer  = inject(DomSanitizer);
  private readonly ngZone     = inject(NgZone);

  // ── Page-level UI state ─────────────────────────────────────────────────────

  /** True when the story detail API call returned an error. */
  readonly hasError = signal(false);

  /** The loaded story detail. Null until the API responds successfully. */
  readonly story = signal<StoryDetailData | null>(null);

  /** True while an add-/remove-reaction API call is in flight. */
  readonly isReacting = signal(false);

  /** The reaction type id that is currently being submitted (shows button-level spinner). */
  readonly pendingReactionType = signal<number | null>(null);

  /** Controls the Full Transcript section visibility. */
  readonly transcriptOpen = signal(true);

  /** Reaction types loaded once from the master list API (typeId = 12). */
  readonly reactionTypes = signal<ReactionTypeMasterItem[]>([]);

  // ── Transcript editor state (owner only) ───────────────────────────────────

  /** Active tab: 1 = Verbatim, 2 = Narrative (matches TranscriptType from API). */
  readonly activeTranscriptTab = signal<1 | 2>(1);

  /** True when the owner has clicked Edit and the textareas are editable. */
  readonly isEditingTranscript = signal(false);

  /** True while the UpdateStoryTranscript API call is in flight. */
  readonly isSavingTranscript = signal(false);

  /** Editable draft for the Verbatim transcript. Populated when story loads. */
  readonly verbatimDraft = signal('');

  /** Editable draft for the Narrative transcript. Populated when story loads. */
  readonly narrativeDraft = signal('');

  // ── Share state (owner only) ───────────────────────────────────────────────

  /** Controls share modal visibility. */
  readonly shareModalOpen = signal(false);

  /** The resolved share URL returned by the API. */
  readonly shareUrl = signal('');

  /** True after the share URL has been copied to clipboard (resets after 2 s). */
  readonly shareCopied = signal(false);

  // ── Download state (owner — Vimeo-ready stories only) ─────────────────────

  /** True while the DownloadVideoFromVimeo API call is in flight. */
  readonly isDownloading = signal(false);

  // ── Vimeo player state ───────────────────────────────────────────────────
  readonly isVimeoPlaying   = signal(false);
  readonly vimeoDuration    = signal(0);
  readonly vimeoProgress    = signal(0);
  readonly vimeoCurrentTime = signal('00:00');
  readonly vimeoTotalTime   = signal('00:00');
  readonly vimeoPlayerReady = signal(false);

  // ── Share-with editor state (owner only) ──────────────────────────────────
  readonly isEditingSharedWith = signal(false);
  readonly familyMemberList    = signal<FamilyMember[]>([]);
  readonly sharedWithDraft     = signal<string[]>([]);
  readonly isLoadingMembers    = signal(false);
  readonly isSavingSharedWith  = signal(false);
  /** Bound via [(ngModel)] — plain property for ngModel compatibility. */
  memberSearchText = '';
  // ── Tag-with editor state (owner only) ────────────────────────────────────
  readonly isEditingTaggedWith = signal(false);
  readonly taggedWithDraft     = signal<string[]>([]);
  readonly isSavingTaggedWith  = signal(false);
  /** Bound via [(ngModel)] — plain property for ngModel compatibility. */
  memberTagSearchText = '';
  // ── Report state ──────────────────────────────────────────────────────────
  readonly reportModalOpen         = signal(false);
  readonly reportReasons           = signal<ReportReasonItem[]>([]);
  readonly selectedReportReasonIds = signal<number[]>([]);
  readonly isLoadingReportReasons  = signal(false);
  readonly isSubmittingReport      = signal(false);
  readonly reportTouched           = signal(false);
  /** Bound via [(ngModel)] — plain property for ngModel compatibility. */
  reportAdditionalDetails = '';

  // ── Derived / computed values ──────────────────────────────────────────────

  /**
   * The reactionType the current user has applied to this story, or null.
   * Derived from the reactions array where isCurrentUser === true.
   */
  readonly currentUserReaction = computed<number | null>(() => {
    const reaction = this.story()?.reactions?.find(r => r.isCurrentUser);
    return reaction?.reactionType ?? null;
  });

  /**
   * The reactionId of the current user's active reaction, or null.
   * Passed as `requestId` to the RemoveReaction API.
   */
  readonly currentUserReactionId = computed<string | null>(() => {
    const reaction = this.story()?.reactions?.find(r => r.isCurrentUser);
    return reaction?.reactionId ?? null;
  });

  /** Family members filtered by memberSearchText. Plain getter for ngModel compatibility. */
  get filteredMembers(): FamilyMember[] {
    const q = this.memberSearchText.toLowerCase().trim();
    const list = this.familyMemberList();
    return q
      ? list.filter(m =>
          m.fullName.toLowerCase().includes(q) ||
          m.relationship.toLowerCase().includes(q),
        )
      : list;
  }

  /** Family members filtered by memberTagSearchText for the tag editor. */
  get filteredTagMembers(): FamilyMember[] {
    const q = this.memberTagSearchText.toLowerCase().trim();
    const list = this.familyMemberList();
    return q
      ? list.filter(m =>
          m.fullName.toLowerCase().includes(q) ||
          m.relationship.toLowerCase().includes(q),
        )
      : list;
  }

  // ── Private fields ───────────────────────────────────────────────────────────────────────────
  /** Route `:id` param — updated reactively via paramMap subscription. */
  private storyId = '';
  private vimeoPlayer: any      = null;
  private progressPollInterval: any = null;

  /** Computed Vimeo embed URL — watched by the constructor effect to init the SDK player. */
  readonly vimeoEmbedUrlComputed = computed((): SafeResourceUrl | null => {
    const s = this.story();
    if (!s?.vimeoVideoId || s.vimeoUploadStatus < 1) return null;
    const hMatch = s.vimeoVideoUrl?.match(/[?&]h=([^&]+)/);
    const hParam  = hMatch ? `&h=${hMatch[1]}` : '';
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://player.vimeo.com/video/${s.vimeoVideoId}?controls=0&dnt=1&api=1${hParam}`,
    );
  });

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────────

  constructor() {
    effect(() => {
      const url = this.vimeoEmbedUrlComputed();
      if (!url) return;
      // Defer until the iframe is in the DOM after Angular renders the @if block
      setTimeout(() => this.initVimeoPlayer(), 50);
    });
  }

  ngOnInit(): void {
    // Load reaction types once — these don’t change per story
    this.loadReactionTypes();
    /**
     * Subscribe to paramMap instead of using snapshot.
     * This ensures the component re-loads data when Angular reuses the
     * same component instance during in-app navigation between related
     * stories (e.g. /app/stories/1 → /app/stories/2).
     */
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.storyId = params.get('id') ?? '';
        // Reset state so previous story doesn't flash while the new one loads
        this.story.set(null);
        this.hasError.set(false);
        this.isEditingTranscript.set(false);
        this.activeTranscriptTab.set(1);
        this.loadStory();
      });
  }

  ngAfterViewInit(): void { /* player init handled by the constructor effect + initVimeoPlayer */ }

  ngOnDestroy(): void {
    this.stopVimeoProgressPoll();
    if (this.vimeoPlayer) {
      this.vimeoPlayer.destroy().catch?.(() => {});
      this.vimeoPlayer = null;
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Data loading
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Fetches available reaction types from the master list API (typeId=12).
   * Called once on component init — types don’t change per story.
   */
  private async loadReactionTypes(): Promise<void> {
    try {
      const resp = await this.api.request<MasterListData>(
        'POST',
        API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
        { typeIds: [12] },
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data?.reactionTypeList?.length) {
        this.reactionTypes.set(resp.data.reactionTypeList);
      }
    } catch { /* silent — fallback: no reaction buttons shown */ }
  }

  /**
   * Fetches the story detail from the API.
   * The global loader (ApiService showLoader) handles the loading indicator.
   * @param silent  When true, suppresses the global loader and error reset
   *                (used for quiet post-reaction refreshes).
   */
  async loadStory(silent = false): Promise<void> {
    if (!this.storyId) return;

    if (!silent) {
      this.hasError.set(false);
    }

    try {
      const resp = await this.api.request<StoryDetailData>(
        'GET',
        `${API_ROUTES.GET_STORY_DETAIL}/${this.storyId}`,
        undefined,
        // showLoader:true delegates the spinner to the global loader service
        { showToaster: false, showLoader: !silent, useToken: true },
      );

      if (resp.status && resp.data) {
        this.story.set(resp.data);
        // Populate transcript drafts so the owner can edit them
        this.verbatimDraft.set(resp.data.transcriptVerbatim?.trim() ?? '');
        this.narrativeDraft.set(resp.data.transcriptNarrative?.trim() ?? '');
      } else if (!silent) {
        this.hasError.set(true);
      }
    } catch {
      if (!silent) this.hasError.set(true);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Reactions
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Toggles a reaction for the current user:
   * - Same type already active → RemoveReaction (DELETE) with body { requestId }.
   * - Different type / no reaction → AddReaction (POST).
   * After the mutation, the story is silently refreshed to show updated counts.
   */
  async toggleReaction(reactionType: number): Promise<void> {
    if (this.isReacting()) return;
    this.isReacting.set(true);
    this.pendingReactionType.set(reactionType);

    // Capture before any async work — signal values may change after awaits
    const existingReactionType = this.currentUserReaction();
    const existingReactionId   = this.currentUserReactionId();

    try {
      if (existingReactionType === reactionType) {
        // User tapped their own active reaction — remove it
        const body: RemoveReactionRequest = { requestId: this.storyId };
        await this.api.request<boolean>(
          'POST',
          API_ROUTES.REMOVE_REACTION,
          body,
          { showToaster: false, showLoader: false, useToken: true },
        );
      } else {
        // If the user already has a different reaction, remove it first so the
        // backend doesn't accumulate two entries for the same user.
        if (existingReactionId) {
          const removeBody: RemoveReactionRequest = { requestId: this.storyId };
          await this.api.request<boolean>(
            'POST',
            API_ROUTES.REMOVE_REACTION,
            removeBody,
            { showToaster: false, showLoader: false, useToken: true },
          );
        }
        // Add the new reaction
        const addBody: AddReactionRequest = { storyId: this.storyId, reactionType };
        await this.api.request<boolean>(
          'POST',
          API_ROUTES.ADD_REACTION,
          addBody,
          { showToaster: false, showLoader: false, useToken: true },
        );
      }

      await this.loadStory(true);
    } finally {
      this.isReacting.set(false);
      this.pendingReactionType.set(null);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────────

  /** Toggles the Full Transcript accordion open/closed. */
  toggleTranscript(): void {
    this.transcriptOpen.update(v => !v);
  }

  /** Sets the active transcript tab (1 = Verbatim, 2 = Narrative). */
  setTranscriptTab(tab: 1 | 2): void {
    this.activeTranscriptTab.set(tab);
  }

  /** Puts the transcript into edit mode. */
  startTranscriptEdit(): void {
    this.isEditingTranscript.set(true);
  }

  /**
   * Saves the active tab's draft to the backend via PUT /Story/UpdateStoryTranscript.
   * TranscriptType: 1 = Verbatim, 2 = Narrative.
   */
  async saveTranscript(): Promise<void> {
    if (this.isSavingTranscript()) return;
    this.isSavingTranscript.set(true);
    const type = this.activeTranscriptTab();
    const description = type === 1 ? this.verbatimDraft() : this.narrativeDraft();
    try {
      const resp = await this.api.request<UpdateTranscriptRequest>(
        'POST',
        API_ROUTES.UPDATE_STORY_TRANSCRIPT,
        { storyId: this.storyId, transcriptType: type, description },
        { showToaster: true, showLoader: false, useToken: true },
      );
      if (resp.status) {
        this.isEditingTranscript.set(false);
        await this.loadStory();
      }
    } catch { /* toaster handles error */ } finally {
      this.isSavingTranscript.set(false);
    }
  }

  /** Navigates the user back to the Stories list. */
  goBack(): void {
    this.router.navigate(['/app/stories']);
  }

  // ── Video player (Vimeo SDK) ──────────────────────────────────────────────────

  get isVimeoReady(): boolean {
    const s = this.story();
    return !!s && s.vimeoUploadStatus >= 1 && !!s.vimeoVideoId;
  }

  /** Proxies the computed signal for template [src] binding. */
  get vimeoEmbedUrl(): SafeResourceUrl | null {
    return this.vimeoEmbedUrlComputed();
  }

  toggleVimeoPlay(): void {
    if (!this.vimeoPlayer) return;
    if (this.isVimeoPlaying()) {
      this.isVimeoPlaying.set(false);
      this.stopVimeoProgressPoll();
      this.vimeoPlayer.pause().catch(() => { this.isVimeoPlaying.set(true); this.startVimeoProgressPoll(); });
    } else {
      this.isVimeoPlaying.set(true);
      this.startVimeoProgressPoll();
      this.vimeoPlayer.play().catch(() => { this.isVimeoPlaying.set(false); this.stopVimeoProgressPoll(); });
    }
  }

  vimeoSeek(event: Event): void {
    if (!this.vimeoPlayer) return;
    const input = event.target as HTMLInputElement;
    const val = Number(input.value);
    this.vimeoPlayer.setCurrentTime(val);
    this.vimeoProgress.set(val);
    const pct = this.vimeoDuration() > 0 ? (val / this.vimeoDuration()) * 100 : 0;
    input.style.setProperty('--seek-pct', `${pct}%`);
  }

  requestVimeoFullscreen(): void {
    this.vimeoPlayer?.requestFullscreen();
  }

  private async initVimeoPlayer(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.vimeoEmbedUrlComputed()) return;
    if (!this.vimeoIframeRef?.nativeElement) return;
    if (this.vimeoPlayer) return;
    try {
      const { default: Player } = await import('@vimeo/player');
      this.vimeoPlayer = new Player(this.vimeoIframeRef.nativeElement);

      this.vimeoPlayer.getDuration().then((d: number) => {
        this.ngZone.run(() => {
          this.vimeoDuration.set(d);
          this.vimeoTotalTime.set(this.formatVimeoTime(d));
          this.vimeoPlayerReady.set(true);
        });
      });

      this.vimeoPlayer.on('timeupdate', (e: any) => {
        this.ngZone.run(() => {
          this.vimeoProgress.set(e.seconds);
          this.vimeoCurrentTime.set(this.formatVimeoTime(e.seconds));
          const seekEl = this.seekBarRef?.nativeElement;
          if (seekEl) {
            seekEl.value = String(e.seconds);
            const pct = this.vimeoDuration() > 0 ? (e.seconds / this.vimeoDuration()) * 100 : 0;
            seekEl.style.setProperty('--seek-pct', `${pct}%`);
          }
        });
      });

      this.vimeoPlayer.on('play',  () => this.ngZone.run(() => { this.isVimeoPlaying.set(true);  this.startVimeoProgressPoll(); }));
      this.vimeoPlayer.on('pause', () => this.ngZone.run(() => { this.isVimeoPlaying.set(false); this.stopVimeoProgressPoll(); }));
      this.vimeoPlayer.on('ended', () => this.ngZone.run(() => { this.isVimeoPlaying.set(false); this.stopVimeoProgressPoll(); }));
    } catch (err) {
      console.error('Vimeo player init error:', err);
    }
  }

  private startVimeoProgressPoll(): void {
    this.stopVimeoProgressPoll();
    this.progressPollInterval = this.ngZone.runOutsideAngular(() =>
      setInterval(() => {
        if (!this.vimeoPlayer) return;
        Promise.all([this.vimeoPlayer.getCurrentTime(), this.vimeoPlayer.getDuration()])
          .then(([seconds, dur]: [number, number]) => {
            this.ngZone.run(() => {
              if (dur > 0 && this.vimeoDuration() === 0) {
                this.vimeoDuration.set(dur);
                this.vimeoTotalTime.set(this.formatVimeoTime(dur));
                this.vimeoPlayerReady.set(true);
              }
              this.vimeoProgress.set(seconds);
              this.vimeoCurrentTime.set(this.formatVimeoTime(seconds));
              const seekEl = this.seekBarRef?.nativeElement;
              if (seekEl) {
                seekEl.value = String(seconds);
                const pct = dur > 0 ? (seconds / dur) * 100 : 0;
                seekEl.style.setProperty('--seek-pct', `${pct}%`);
              }
            });
          }).catch(() => {});
      }, 250)
    );
  }

  private stopVimeoProgressPoll(): void {
    if (this.progressPollInterval != null) {
      clearInterval(this.progressPollInterval);
      this.progressPollInterval = null;
    }
  }

  private formatVimeoTime(sec: number): string {
    const total = Math.max(0, Math.floor(sec));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  // ── Share-with editor ──────────────────────────────────────────────────────

  startEditSharedWith(): void {
    const current = this.story()?.sharedWithPeople ?? [];
    this.sharedWithDraft.set(current.map(p => p.userId));
    this.memberSearchText = '';
    this.isEditingSharedWith.set(true);
    if (this.familyMemberList().length === 0) {
      void this.loadFamilyMembers();
    }
  }

  cancelEditSharedWith(): void {
    this.isEditingSharedWith.set(false);
    this.sharedWithDraft.set([]);
    this.memberSearchText = '';
  }

  private async loadFamilyMembers(): Promise<void> {
    this.isLoadingMembers.set(true);
    try {
      const resp = await this.api.request<FamilyMember[]>(
        'GET',
        API_ROUTES.GET_FAMILY_MEMBER_LIST,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data) {
        this.familyMemberList.set(resp.data);
      }
    } catch { /* silent */ } finally {
      this.isLoadingMembers.set(false);
    }
  }

  toggleSharedMember(memberId: string): void {
    const current = this.sharedWithDraft();
    this.sharedWithDraft.set(
      current.includes(memberId)
        ? current.filter(id => id !== memberId)
        : [...current, memberId],
    );
  }

  isMemberSelected(memberId: string): boolean {
    return this.sharedWithDraft().includes(memberId);
  }

  getMemberName(memberId: string): string {
    const fromList = this.familyMemberList().find(m => m.familyMemberId === memberId)?.fullName;
    if (fromList) return fromList;
    const story = this.story();
    return story?.sharedWithPeople?.find(p => p.userId === memberId)?.fullName
      ?? story?.taggedPeople?.find(p => p.familyMemberId === memberId)?.fullName
      ?? memberId;
  }

  async saveSharedWith(): Promise<void> {
    if (this.isSavingSharedWith()) return;
    const s = this.story();
    if (!s) return;
    this.isSavingSharedWith.set(true);
    try {
      const body: ShareStoryWithMembersRequest = {
        storyId:           s.storyId,
        sharedWithUserIds: this.sharedWithDraft(),
      };
      const resp = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SHARE_STORY_WITH_MEMBERS,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
      if (resp.status) {
        this.isEditingSharedWith.set(false);
        this.memberSearchText = '';
        await this.loadStory(true);
      }
    } catch { /* error toast shown by ApiService */ } finally {
      this.isSavingSharedWith.set(false);
    }
  }

  // ── Tag-with editor ─────────────────────────────────────────────────────────────────

  startEditTaggedWith(): void {
    const current = this.story()?.taggedPeople ?? [];
    this.taggedWithDraft.set(current.map(p => p.familyMemberId));
    this.memberTagSearchText = '';
    this.isEditingTaggedWith.set(true);
    if (this.familyMemberList().length === 0) {
      void this.loadFamilyMembers();
    }
  }

  cancelEditTaggedWith(): void {
    this.isEditingTaggedWith.set(false);
    this.taggedWithDraft.set([]);
    this.memberTagSearchText = '';
  }

  toggleTaggedMember(memberId: string): void {
    const current = this.taggedWithDraft();
    this.taggedWithDraft.set(
      current.includes(memberId)
        ? current.filter(id => id !== memberId)
        : [...current, memberId],
    );
  }

  isTaggedMemberSelected(memberId: string): boolean {
    return this.taggedWithDraft().includes(memberId);
  }

  async saveTaggedWith(): Promise<void> {
    if (this.isSavingTaggedWith()) return;
    const s = this.story();
    if (!s) return;
    this.isSavingTaggedWith.set(true);
    try {
      const body: TagStoryWithMembersRequest = {
        storyId:         s.storyId,
        tagdWithUserIds: this.taggedWithDraft(),
      };
      const resp = await this.api.request<boolean>(
        'POST',
        API_ROUTES.TAG_STORY_WITH_MEMBERS,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
      if (resp.status) {
        this.isEditingTaggedWith.set(false);
        this.memberTagSearchText = '';
        await this.loadStory(true);
      }
    } catch { /* error toast shown by ApiService */ } finally {
      this.isSavingTaggedWith.set(false);
    }
  }

  // ── Share ──────────────────────────────────────────────────────────────────

  /**
   * Opens the share modal using the shareUrl already loaded with the story detail.
   * No extra API call needed.
   */
  openShareModal(): void {
    const raw = this.story()?.shareUrl ?? '';
    if (!raw) return;
    const fullUrl = raw.startsWith('http') ? raw : `${window.location.origin}${raw}`;
    this.shareUrl.set(fullUrl);
    this.shareCopied.set(false);
    this.shareModalOpen.set(true);
  }

  /** Closes the share modal and resets state. */
  closeShareModal(): void {
    this.shareModalOpen.set(false);
    this.shareUrl.set('');
    this.shareCopied.set(false);
  }

  // ── Download ───────────────────────────────────────────────────────────────

  /**
   * Requests a Vimeo download URL from the backend and opens it in a new tab.
   * For Vimeo-ready stories: calls DownloadVideoFromVimeo API — the backend message is shown via toast.
   * For native mediaUrl stories: triggers a direct browser download.
   */
  async downloadVideo(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.isDownloading()) return;
    const s = this.story();
    if (!s) return;

    this.isDownloading.set(true);
    try {
      if (s.vimeoVideoId) {
        const resp = await this.api.request(
          'GET',
          `${API_ROUTES.DOWNLOAD_VIDEO_FROM_VIMEO}/${s.vimeoVideoId}`,
          undefined,
          { showToaster: false, showLoader: true, useToken: true },
        );

        // data may be { downloadLink, downloadUrl, url } or a plain string
        const data = resp?.['data'] as any;
        let downloadLink = '';
        if (data !== null && typeof data === 'object') {
          downloadLink = (data['downloadLink'] ?? data['downloadUrl'] ?? data['url'] ?? '') as string;
        } else if (typeof data === 'string') {
          downloadLink = data;
        }

        if (downloadLink) {
          const a = document.createElement('a');
          a.href = downloadLink;
          a.download = s.title || 'story-video';
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        return;
      }

      // Native mediaUrl fallback
      if (s.mediaUrl) {
        const a    = document.createElement('a');
        a.href     = s.mediaUrl;
        a.download = s.title || 'story-video';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch { /* error toast shown by api service */ } finally {
      this.isDownloading.set(false);
    }
  }

  // ── Report ─────────────────────────────────────────────────────────────────

  openReportModal(): void {
    this.selectedReportReasonIds.set([]);
    this.reportAdditionalDetails = '';
    this.reportTouched.set(false);
    this.reportModalOpen.set(true);
    if (this.reportReasons().length === 0) {
      this.loadReportReasons();
    }
  }

  closeReportModal(): void {
    this.reportModalOpen.set(false);
  }

  private async loadReportReasons(): Promise<void> {
    this.isLoadingReportReasons.set(true);
    try {
      const resp = await this.api.request<MasterListData>(
        'POST',
        API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
        { typeIds: [13] },
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data?.reportReasonList?.length) {
        this.reportReasons.set(resp.data.reportReasonList);
      }
    } catch { /* silent */ } finally {
      this.isLoadingReportReasons.set(false);
    }
  }

  toggleReportReason(id: number): void {
    const current = this.selectedReportReasonIds();
    this.selectedReportReasonIds.set(
      current.includes(id)
        ? current.filter(r => r !== id)
        : [...current, id],
    );
  }

  isReasonSelected(id: number): boolean {
    return this.selectedReportReasonIds().includes(id);
  }

  async submitReport(): Promise<void> {
    if (this.isSubmittingReport()) return;
    this.reportTouched.set(true);
    if (!this.selectedReportReasonIds().length) return;
    const s = this.story();
    if (!s) return;
    this.isSubmittingReport.set(true);
    try {
      const resp = await this.api.request(
        'POST',
        API_ROUTES.REPORT_STORY,
        {
          storyId:           s.storyId,
          reportReasonIds:   this.selectedReportReasonIds(),
          additionalDetails: this.reportAdditionalDetails,
        },
        { showToaster: true, showLoader: true, useToken: true },
      );
    } catch { /* toaster handles error message from backend */ } finally {
      this.isSubmittingReport.set(false);
      this.closeReportModal();
    }
  }

  /** Copies the share URL to the clipboard and shows a brief confirmation. */
  async copyShareLink(): Promise<void> {
    const url = this.shareUrl();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this.shareCopied.set(true);
      setTimeout(() => this.shareCopied.set(false), 2000);
    } catch { /* browser may block clipboard without HTTPS */ }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Template helpers
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Returns the correct transcript text based on the story's
   * selectedTranscriptType (0 = Verbatim, 1 = Narrative).
   * Returns an empty string if neither transcript has content.
   */
  getTranscriptText(): string {
    const s: StoryDetailData | null = this.story();
    if (!s) return '';
    if (s.selectedTranscriptType === 1) return s.transcriptNarrative?.trim() ?? '';
    return s.transcriptVerbatim?.trim() ?? '';
  }

  /**
   * Returns the total count of reactions for the given reactionType from the backend data.
   */
  getReactionCount(type: number): number {
    return this.story()?.reactions?.filter(r => r.reactionType === type).length ?? 0;
  }

  /**
   * Returns a zero-padded 2-character count string for display
   * (e.g. 8 → "08", 12 → "12").
   */
  formatReactionCount(type: number): string {
    return this.getReactionCount(type).toString().padStart(2, '0');
  }

  /**
   * Returns the full names of all tagged people joined by ", ".
   * Displayed in the Story Details sidebar.
   */
  getTaggedPeopleNames(): string {
    return (this.story()?.taggedPeople ?? []).map(p => p.fullName).join(', ');
  }

  /**
   * Returns the full names of all people the story was shared with, joined by ", ".
   * Displayed in the Story Details sidebar.
   */
  getSharedWithPeopleNames(): string {
    return (this.story()?.sharedWithPeople ?? []).map(p => p.fullName).join(', ');
  }

  /** Returns the display emoji for a given reaction type id. */
  getReactionEmoji(id: number): string {
    return REACTION_EMOJI[id] ?? '👍';
  }

  /** Returns the SVG icon path for a given reaction type name (from master list API). */
  getReactionIcon(name: string): string {
    const icons: Record<string, string> = {
      'heart':     'images/icons/fm-heart-cl.svg',
      'like':      'images/icons/fm-love.svg',
      'celebrate': 'images/icons/fm-celebrate.svg',
      'support':   'images/icons/fm-support.svg',
    };
    return icons[name.toLowerCase().trim()] ?? 'images/icons/fm-heart-cl.svg';
  }

  /** Returns all reactions of a given type from the story's reactions array. */
  getReactionUsers(type: number): StoryReaction[] {
    return this.story()?.reactions?.filter(r => r.reactionType === type) ?? [];
  }

  /** Returns up to 2 uppercase initials from a display name. */
  getReactionUserInitials(name: string): string {
    return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
  }

  /**
   * Formats a duration in seconds to "M:SS" (e.g. 90 → "1:30").
   */
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0:00';
    const m   = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  /**
   * Formats an ISO date string (e.g. "2026-05-28") to a long readable date
   * (e.g. "May 28, 2026").
   */
  formatStoryDate(dateString: string): string {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  /**
   * Converts an ISO datetime string to a human-readable relative label
   * (e.g. "Today", "2 days ago", "1 week ago").
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0)  return 'Today';
    if (diffDays === 1)  return '1 day ago';
    if (diffDays < 7)   return `${diffDays} days ago`;
    if (diffDays < 14)  return '1 week ago';
    if (diffDays < 30)  return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60)  return '1 month ago';
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    if (diffDays < 730) return '1 year ago';
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}

